import React, { useState, useEffect, useRef } from 'react';
import { Table, Form, Input, Button, Space, Select, DatePicker, Tag, Drawer, InputNumber, Alert, Spin, Descriptions, Modal, Tooltip, message } from 'antd';
import { SearchOutlined, ReloadOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getOrderList, createOrder, updateOrder } from '../../api/order';
import { getPlatformList, getModelList } from '../../api/config';
import { getSerialList } from '../../api/serial';
import { useVisibleFields, hasPerm } from '../../hooks/usePermission';
import { getFieldMap } from '../../constants/fieldResource';
import { downloadCsv } from '../../utils/exportCsv';

const { RangePicker } = DatePicker;

const PURPOSE_OPTIONS = ['研发', '试产', '量产PO', '售后'].map((v) => ({ value: v, label: v }));
const FACTORY_OPTIONS = ['达天', '凯木金', '中嘉'].map((v) => ({ value: v, label: v }));
const MODEL_OPTIONS = ['YLF-X1', 'YLF-G2', 'YLF-C3', 'YLF-T4'].map((v) => ({ value: v, label: v }));
const STATUS_OPTIONS = ['待生产', '生产中', '已完成', '已关闭'].map((v) => ({ value: v, label: v }));

const STATUS_COLOR = { '待生产': 'default', '生产中': 'processing', '已完成': 'success', '已关闭': 'error' };

const BOUND_STATUSES = ['已绑定', '已发货', '已退货'];

const AUTH_STATUS_OPTIONS = ['未授权', '已授权(下发)'].map((v) => ({ value: v, label: v }));
const BIND_STATUS_OPTIONS = ['未绑定', '已绑定'].map((v) => ({ value: v, label: v }));

const AUTH_STATUS_COLOR = { '未授权': 'default', '已授权(下发)': 'processing' };
const BIND_STATUS_COLOR = { '未绑定': 'default', '已绑定': 'success' };

const getAuthStatus = (s) => (s === '未使用' ? '未授权' : '已授权(下发)');
const getBindStatus = (s) => (BOUND_STATUSES.includes(s) ? '已绑定' : '未绑定');

const OrderList = () => {
    const [form] = Form.useForm();
    const [createForm] = Form.useForm();
    const [editForm] = Form.useForm();
    const watchPlatform = Form.useWatch('platform', createForm);
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editOrder, setEditOrder] = useState(null);
    const [editing, setEditing] = useState(false);
    const [platformOptions, setPlatformOptions] = useState([]);
    const [platformKeyFieldMap, setPlatformKeyFieldMap] = useState({});
    const [modelOptions, setModelOptions] = useState([]);
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailOrder, setDetailOrder] = useState(null);
    const [detailSerials, setDetailSerials] = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailSearchForm] = Form.useForm();
    const [detailFilter, setDetailFilter] = useState({});
    const stockRef = useRef(110);

    const fetchList = async (params = {}) => {
        setLoading(true);
        try {
            const res = await getOrderList({ page: params.page || 1, page_size: params.page_size || 10, ...params });
            if (res.code === 0) {
                const sorted = [...res.data.list].sort((a, b) => b.create_time.localeCompare(a.create_time));
                setDataSource(sorted);
                setPagination((prev) => ({ ...prev, total: res.data.total }));
            } else {
                message.error(res.message || '获取列表失败');
            }
        } catch (error) {
            console.error('Fetch order list failed:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    useEffect(() => {
        const fetchPlatforms = async () => {
            const res = await getPlatformList();
            if (res.code === 0) {
                setPlatformOptions(res.data.list.map((p) => ({ value: p.name, label: p.name })));
                const map = {};
                res.data.list.forEach((p) => {
                    map[p.name] = ((p.fields || []).find((f) => f.is_key) || {}).name;
                });
                setPlatformKeyFieldMap(map);
            }
        };
        fetchPlatforms();
    }, []);

    useEffect(() => {
        if (watchPlatform) {
            const fetchModels = async () => {
                const res = await getModelList({ platform: watchPlatform });
                if (res.code === 0) {
                    setModelOptions(res.data.list.map((m) => ({ value: m.name, label: m.name })));
                }
            };
            fetchModels();
            createForm.setFieldsValue({ model: undefined });
        } else {
            setModelOptions([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchPlatform]);

    const handleSearch = () => {
        fetchList({ ...form.getFieldsValue(), page: 1 });
    };

    const handleReset = () => {
        form.resetFields();
        fetchList({ page: 1 });
    };

    const handleCreate = async () => {
        const values = await createForm.validateFields();
        setCreating(true);
        try {
            const res = await createOrder(values);
            if (res.code === 0) {
                setDrawerOpen(false);
                createForm.resetFields();
                const allocated = Math.min(values.quantity, stockRef.current);
                stockRef.current -= allocated;
                if (allocated < values.quantity) {
                    message.warning(`工单创建成功，库存不足，仅分配 ${allocated}/${values.quantity} 串码`);
                } else {
                    message.success(`工单创建成功，已分配 ${allocated}/${values.quantity} 串码`);
                }
                const newOrder = {
                    id: `new_${Date.now()}`,
                    order_no: `PO${Date.now()}`,
                    remark: values.remark,
                    purpose: values.purpose,
                    factory: values.factory,
                    platform: values.platform,
                    model: values.model,
                    quantity: values.quantity,
                    allocated: 0,
                    authorized: 0,
                    bound: 0,
                    shipped: 0,
                    returned: 0,
                    status: '待生产',
                    creator: 'admin',
                    create_time: dayjs().format('YYYY-MM-DD HH:mm:ss'),
                    allocating: true,
                };
                setDataSource((prev) => [newOrder, ...prev]);
                setPagination((prev) => ({ ...prev, total: prev.total + 1 }));
                animateAllocation(newOrder.id, allocated);
            } else {
                message.error(res.message || '创建失败');
            }
        } catch (error) {
            if (error.errorFields) return;
            console.error('Create order failed:', error);
        } finally {
            setCreating(false);
        }
    };

    const animateAllocation = (id, target) => {
        if (target <= 0) {
            setDataSource((prev) => prev.map((item) => (item.id === id ? { ...item, allocating: false } : item)));
            return;
        }
        const duration = 1200;
        const start = performance.now();
        const tick = (now) => {
            const progress = Math.min((now - start) / duration, 1);
            const value = Math.round(target * progress);
            setDataSource((prev) => prev.map((item) => (item.id === id ? { ...item, allocated: value } : item)));
            if (progress < 1) {
                requestAnimationFrame(tick);
            } else {
                setDataSource((prev) => prev.map((item) => (item.id === id ? { ...item, allocating: false } : item)));
            }
        };
        requestAnimationFrame(tick);
    };

    const handleExport = () => {
        const fields = getFieldMap()['serial:list'] || [];
        const cols = fields
            .filter((f) => serialVisibleFields.has(f.code) && f.code !== 'key')
            .map((f) => {
                if (f.code === 'fields') {
                    return {
                        label: f.label,
                        value: (r) => {
                            const keyField = platformKeyFieldMap[r.platform];
                            return Object.entries(r.fields || {})
                                .filter(([k]) => k !== keyField)
                                .map(([k, v]) => `${k}:${v}`)
                                .join('; ');
                        },
                    };
                }
                return { label: f.label, value: (r) => r[f.code] ?? '' };
            });
        downloadCsv(`${detailOrder?.order_no || '工单'}_串码.csv`, cols, filteredSerials);
    };

    const handleEdit = (record) => {
        setEditOrder(record);
        editForm.setFieldsValue({
            remark: record.remark,
            purpose: record.purpose,
            factory: record.factory,
        });
        setEditOpen(true);
    };

    const handleEditSubmit = async () => {
        const values = await editForm.validateFields();
        setEditing(true);
        try {
            const res = await updateOrder({ id: editOrder.id, ...values });
            if (res.code === 0) {
                setDataSource((prev) => prev.map((item) => (item.id === editOrder.id ? { ...item, ...values } : item)));
                message.success('保存成功');
                setEditOpen(false);
            } else {
                message.error(res.message || '保存失败');
            }
        } catch (error) {
            if (error.errorFields) return;
            console.error('Update order failed:', error);
        } finally {
            setEditing(false);
        }
    };

    const handleClose = (record) => {
        Modal.confirm({
            title: '关闭确认',
            content: `确定关闭工单 ${record.order_no} 吗？`,
            okText: '确认关闭',
            okButtonProps: { danger: true },
            cancelText: '取消',
            centered: true,
            onOk: () => {
                setDataSource((prev) => prev.map((item) => (item.id === record.id ? { ...item, status: '已关闭', allocated: 0, authorized: 0, bound: 0, shipped: 0, returned: 0 } : item)));
                message.success('工单已关闭');
            },
        });
    };

    const handleDetail = async (record) => {
        setDetailOrder(record);
        detailSearchForm.resetFields();
        setDetailFilter({});
        setDetailOpen(true);
        setDetailLoading(true);
        try {
            const res = await getSerialList({ order_no: record.order_no });
            if (res.code === 0) {
                setDetailSerials(res.data.list);
            } else {
                setDetailSerials([]);
            }
        } catch (error) {
            console.error('Fetch order serials failed:', error);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleDetailSearch = () => {
        setDetailFilter(detailSearchForm.getFieldsValue());
    };

    const handleDetailReset = () => {
        detailSearchForm.resetFields();
        setDetailFilter({});
    };

    const visibleFields = useVisibleFields('order:list');
    const show = (code) => visibleFields.has(code);
    const serialVisibleFields = useVisibleFields('serial:list');
    const showSerial = (code) => serialVisibleFields.has(code);

    const filteredSerials = detailSerials.filter((s) => {
        const mac = (detailFilter.mac || '').trim().toLowerCase();
        const sn = (detailFilter.sn || '').trim().toLowerCase();
        const authStatus = detailFilter.auth_status;
        const bindStatus = detailFilter.bind_status;
        return (!mac || (s.mac || '').toLowerCase().includes(mac))
            && (!sn || (s.sn || '').toLowerCase().includes(sn))
            && (!authStatus || getAuthStatus(s.status) === authStatus)
            && (!bindStatus || getBindStatus(s.status) === bindStatus);
    });

    const detailSerialColumns = [
        showSerial('mac') && { title: 'MAC', dataIndex: 'mac', key: 'mac', width: 180, render: (v) => <div style={{ whiteSpace: 'pre-wrap' }}>{v || '-'}</div> },
        showSerial('sn') && { title: 'SN', dataIndex: 'sn', key: 'sn', width: 150 },
        showSerial('platform') && { title: '平台', dataIndex: 'platform', key: 'platform', width: 90 },
        showSerial('fields') && {
            title: '平台字段', dataIndex: 'fields', key: 'fields', width: 220,
            render: (fields, record) => {
                const keyField = platformKeyFieldMap[record.platform];
                const entries = Object.entries(fields || {}).filter(([k]) => k !== keyField);
                return (
                    <Space wrap size={4}>
                        {entries.map(([k, v]) => (
                            <Tag key={k}>{k}：{v}</Tag>
                        ))}
                    </Space>
                );
            },
        },
        showSerial('auth_status') && {
            title: '授权状态', dataIndex: 'status', key: 'auth_status', width: 120,
            render: (v) => { const t = getAuthStatus(v); return <Tag color={AUTH_STATUS_COLOR[t] || 'default'}>{t}</Tag>; },
        },
        showSerial('bind_status') && {
            title: '绑定状态', dataIndex: 'status', key: 'bind_status', width: 110,
            render: (v) => { const t = getBindStatus(v); return <Tag color={BIND_STATUS_COLOR[t] || 'default'}>{t}</Tag>; },
        },
        showSerial('cpu_id') && { title: 'CPUID', dataIndex: 'cpu_id', key: 'cpu_id', width: 120, render: (v) => v || '-' },
    ].filter(Boolean);

    const columns = [
        show('order_no') && { title: '工单号', dataIndex: 'order_no', key: 'order_no', width: 140 },
        show('purpose') && { title: '串码用途', dataIndex: 'purpose', key: 'purpose', width: 100 },
        show('factory') && { title: '生产工厂', dataIndex: 'factory', key: 'factory', width: 100 },
        show('platform') && { title: '运营平台', dataIndex: 'platform', key: 'platform', width: 100 },
        show('model') && { title: '设备型号', dataIndex: 'model', key: 'model', width: 110 },
        show('status') && {
            title: '工单状态', dataIndex: 'status', key: 'status', width: 100,
            render: (v) => <Tag color={STATUS_COLOR[v] || 'default'}>{v}</Tag>,
        },
        show('allocated') && {
            title: '已分配/需求产量', key: 'allocated', width: 140,
            render: (_, record) => {
                const text = `${record.allocated}/${record.quantity}`;
                const full = record.allocated >= record.quantity;
                if (record.allocating) {
                    return <Space size={6}><Spin size="small" />{text}</Space>;
                }
                return <Tag color={full ? 'green' : 'orange'}>{text}</Tag>;
            },
        },
        show('produced') && {
            title: (
                <span>
                    已生产
                    <Tooltip title="包含已授权、已绑定、已发货、已退货状态">
                        <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
                    </Tooltip>
                </span>
            ),
            key: 'produced', width: 100,
            render: (_, record) => record.authorized + record.bound + (record.shipped || 0) + (record.returned || 0),
        },
        show('remark') && {
            title: '备注', dataIndex: 'remark', key: 'remark', width: 140,
            render: (v) => (v ? (
                <Tooltip title={v} placement="topLeft">
                    <span style={{ display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</span>
                </Tooltip>
            ) : '-'),
        },
        show('creator') && { title: '创建人', dataIndex: 'creator', key: 'creator', width: 100 },
        show('create_time') && { title: '创建时间', dataIndex: 'create_time', key: 'create_time', width: 170 },
        {
            title: '操作', key: 'action',
            onHeaderCell: () => ({ style: { textAlign: 'center' } }),
            render: (_, record) => (
                <Space size={0}>
                    {hasPerm('order:edit') && (
                        <Button type="link" size="small" style={{ padding: '0 6px' }} onClick={() => handleEdit(record)}>编辑</Button>
                    )}
                    {hasPerm('order:serial') && (
                        <Button type="link" size="small" style={{ padding: '0 6px' }} onClick={() => handleDetail(record)}>查看串码</Button>
                    )}
                    {hasPerm('order:close') && record.status === '待生产' && (
                        <Button type="link" size="small" danger style={{ padding: '0 6px' }} onClick={() => handleClose(record)}>关闭</Button>
                    )}
                </Space>
            ),
        },
    ].filter(Boolean);

    return (
        <div style={{ padding: '16px' }}>
            <Form form={form} layout="inline" style={{ marginBottom: 16, rowGap: 16 }}>
                <Form.Item name="order_no">
                    <Input placeholder="工单号" allowClear style={{ width: 150 }} />
                </Form.Item>
                <Form.Item name="factory">
                    <Select placeholder="生产工厂" allowClear options={FACTORY_OPTIONS} style={{ width: 120 }} />
                </Form.Item>
                <Form.Item name="model">
                    <Select placeholder="设备型号" allowClear options={MODEL_OPTIONS} style={{ width: 130 }} />
                </Form.Item>
                <Form.Item name="status">
                    <Select placeholder="工单状态" allowClear options={STATUS_OPTIONS} style={{ width: 120 }} />
                </Form.Item>
                <Form.Item name="date_range">
                    <RangePicker placeholder={['开始日期', '结束日期']} style={{ width: 240 }} />
                </Form.Item>
                <Form.Item>
                    <Space>
                        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
                        <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
                    </Space>
                </Form.Item>
            </Form>

            <div style={{ marginBottom: 16 }}>
                {hasPerm('order:create') && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => { createForm.resetFields(); setDrawerOpen(true); }}>创建工单</Button>
                )}
            </div>

            <Table
                rowKey="id"
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                pagination={pagination}
                scroll={{ x: 1200 }}
            />

            <Drawer
                title="创建生产工单"
                width={480}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                footer={
                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setDrawerOpen(false)}>取消</Button>
                            <Button type="primary" loading={creating} onClick={handleCreate}>创建</Button>
                        </Space>
                    </div>
                }
            >
                <Form form={createForm} layout="vertical" initialValues={{ quantity: 100 }}>
                    <Form.Item name="remark" label="备注">
                        <Input.TextArea placeholder="请输入备注" rows={2} maxLength={200} />
                    </Form.Item>
                    <Form.Item name="purpose" label="串码用途" rules={[{ required: true, message: '请选择串码用途' }]}>
                        <Select placeholder="请选择" options={PURPOSE_OPTIONS} />
                    </Form.Item>
                    <Form.Item name="factory" label="生产工厂" rules={[{ required: true, message: '请选择生产工厂' }]}>
                        <Select placeholder="请选择" options={FACTORY_OPTIONS} />
                    </Form.Item>
                    <Form.Item name="platform" label="运营平台" rules={[{ required: true, message: '请选择运营平台' }]}>
                        <Select placeholder="请选择" options={platformOptions} />
                    </Form.Item>
                    <Form.Item name="model" label="设备型号" rules={[{ required: true, message: '请选择设备型号' }]}>
                        <Select placeholder="请选择" options={modelOptions} disabled={!watchPlatform} />
                    </Form.Item>
                    <Form.Item name="quantity" label="需求产量" rules={[{ required: true, message: '请输入需求产量' }]}>
                        <InputNumber min={1} style={{ width: '100%' }} placeholder="请输入需求产量" />
                    </Form.Item>
                </Form>
                <Alert
                    type="warning"
                    showIcon
                    message="创建工单后，系统将根据「需求产量」自动从库存中锁定并预分配串码，防止超发"
                />
            </Drawer>

            <Drawer
                title={`编辑工单 - ${editOrder?.order_no || ''}`}
                width={480}
                open={editOpen}
                onClose={() => setEditOpen(false)}
                footer={
                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setEditOpen(false)}>取消</Button>
                            <Button type="primary" loading={editing} onClick={handleEditSubmit}>保存</Button>
                        </Space>
                    </div>
                }
            >
                <Form form={editForm} layout="vertical">
                    <Form.Item name="remark" label="备注">
                        <Input.TextArea placeholder="请输入备注" rows={2} maxLength={200} />
                    </Form.Item>
                    <Form.Item name="purpose" label="串码用途" rules={[{ required: true, message: '请选择串码用途' }]}>
                        <Select placeholder="请选择" options={PURPOSE_OPTIONS} />
                    </Form.Item>
                    <Form.Item name="factory" label="生产工厂" rules={[{ required: true, message: '请选择生产工厂' }]}>
                        <Select placeholder="请选择" options={FACTORY_OPTIONS} />
                    </Form.Item>
                </Form>
            </Drawer>

            <Drawer
                title={`工单详情 - ${detailOrder?.order_no || ''}`}
                width="70%"
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
            >
                {detailOrder && (
                    <>
                        <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
                            {show('order_no') && <Descriptions.Item label="工单号">{detailOrder.order_no}</Descriptions.Item>}
                            {show('remark') && <Descriptions.Item label="备注">{detailOrder.remark || '-'}</Descriptions.Item>}
                            {show('purpose') && <Descriptions.Item label="串码用途">{detailOrder.purpose}</Descriptions.Item>}
                            {show('factory') && <Descriptions.Item label="生产工厂">{detailOrder.factory}</Descriptions.Item>}
                            {show('platform') && <Descriptions.Item label="运营平台">{detailOrder.platform}</Descriptions.Item>}
                            {show('model') && <Descriptions.Item label="设备型号">{detailOrder.model}</Descriptions.Item>}
                            {show('allocated') && <Descriptions.Item label="需求产量">{detailOrder.quantity}</Descriptions.Item>}
                            {show('allocated') && <Descriptions.Item label="已分配串码">{detailOrder.allocated}</Descriptions.Item>}
                            {show('status') && (
                                <Descriptions.Item label="状态">
                                    <Tag color={STATUS_COLOR[detailOrder.status] || 'default'}>{detailOrder.status}</Tag>
                                </Descriptions.Item>
                            )}
                            {show('creator') && <Descriptions.Item label="创建人">{detailOrder.creator}</Descriptions.Item>}
                            {show('create_time') && <Descriptions.Item label="创建时间">{detailOrder.create_time}</Descriptions.Item>}
                        </Descriptions>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                            <Form form={detailSearchForm} layout="inline" style={{ rowGap: 12 }}>
                                <Form.Item name="mac">
                                    <Input placeholder="MAC" allowClear style={{ width: 160 }} />
                                </Form.Item>
                                <Form.Item name="sn">
                                    <Input placeholder="SN" allowClear style={{ width: 160 }} />
                                </Form.Item>
                                <Form.Item name="auth_status">
                                    <Select placeholder="授权状态" allowClear options={AUTH_STATUS_OPTIONS} style={{ width: 130 }} />
                                </Form.Item>
                                <Form.Item name="bind_status">
                                    <Select placeholder="绑定状态" allowClear options={BIND_STATUS_OPTIONS} style={{ width: 120 }} />
                                </Form.Item>
                                <Form.Item>
                                    <Space>
                                        <Button type="primary" icon={<SearchOutlined />} onClick={handleDetailSearch}>查询</Button>
                                        <Button icon={<ReloadOutlined />} onClick={handleDetailReset}>重置</Button>
                                    </Space>
                                </Form.Item>
                            </Form>
                            {hasPerm('order:export') && (
                                <Button size="small" onClick={handleExport}>导出</Button>
                            )}
                        </div>

                        <Table
                            rowKey="id"
                            columns={detailSerialColumns}
                            dataSource={filteredSerials}
                            loading={detailLoading}
                            pagination={{ pageSize: 5, size: 'small', showTotal: (total) => `共 ${total} 条` }}
                            size="small"
                            scroll={{ x: 720 }}
                        />
                    </>
                )}
            </Drawer>
        </div>
    );
};

export default OrderList;
